import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useUser } from '@/contexts/UserContext';
import { api } from '@/config/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { DataTable, DataTableColumn, DataTableGroup, DataTableConfig } from '@/components/ui/data-table';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Calendar,
  MapPin,
  Users,
  Eye,
  Edit,
  Upload,
  Plus,
  Trash2,
  Search,
  Filter,
  Camera,
  User,
  UserCheck,
  X,
  AlertCircle
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { Event } from '@/types/schema';

interface Photographer {
  id: string;
  name: string;
  email: string;
}

interface EventWithRelationship extends Event {
  isOwner?: boolean;
  isSubscribed?: boolean;
}

interface SubscribedEvent extends Event {
  subscriptionStatus: string;
  subscriptionId: string;
}

interface PendingSubscription {
  id: string;
  eventId: string;
  photographerId: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  event: {
    id: string;
    title: string;
    date: string;
    location: string;
    coverimage?: string;
  } | null;
  photographer: {
    id: string;
    name: string;
    email: string;
  };
}

export default function EventsManagement() {
  const { user, isLoading } = useUser();
  const [, setLocation] = useLocation();
  const [events, setEvents] = useState<EventWithRelationship[]>([]);
  const [pendingSubscriptions, setPendingSubscriptions] = useState<PendingSubscription[]>([]);
  const [subscribedEvents, setSubscribedEvents] = useState<SubscribedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [photographers, setPhotographers] = useState<Photographer[]>([]);
  const [selectedPhotographer, setSelectedPhotographer] = useState<string>('all');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!isLoading && !user) {
      setLocation('/login');
    }
  }, [isLoading, user, setLocation]);

  useEffect(() => {
    if (user?.id) {
      // Detectar se é admin
      const userEmail = user.email?.toLowerCase() || '';
      const adminEmails = ['admin@fotup.com', 'admin@devonno.com'];
      const adminStatus = adminEmails.includes(userEmail);
      setIsAdmin(adminStatus);

      fetchEvents(adminStatus);
      
      if (adminStatus) {
        fetchPendingSubscriptions();
        fetchPhotographers();
      } else {
        fetchSubscribedEvents();
      }
    }
  }, [user?.id]);

  const fetchEvents = async (adminStatus: boolean, photographerId: string = 'all') => {
    try {
      setLoading(true);

      if (!user?.id) return;

      let response;

      // Se é admin e quer ver todos (ou filtro específico)
      if (adminStatus) {
        if (photographerId === 'all') {
          // Buscar todos os eventos do sistema
          response = await api.get('/api/events?limit=100');
        } else {
          // Buscar eventos de um fotógrafo específico
          response = await api.get(`/api/events/photographer/${photographerId}`);
        }
      } else {
        // Usuário normal vê apenas seus eventos
        response = await api.get(`/api/events/photographer/${user.id}`);
      }

      if (response.data.success) {
        // Garantir que os eventos tenham as propriedades isOwner e isSubscribed
        const eventsData = (response.data.data || []).map((event: any) => ({
          ...event,
          isOwner: event.isOwner ?? (event.createdbyuserid === user?.id),
          isSubscribed: event.isSubscribed ?? false,
        }));
        setEvents(eventsData);
      }
    } catch (error) {
      console.error('Erro ao buscar eventos:', error);
      toast({
        title: 'Erro ao carregar eventos',
        description: 'Não foi possível carregar a lista de eventos.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingSubscriptions = async () => {
    try {
      const response = await api.get('/api/events/subscriptions/pending');
      if (response.data.success) {
        setPendingSubscriptions(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao buscar inscrições pendentes:', error);
    }
  };

  const fetchSubscribedEvents = async () => {
    if (!user?.id) return;
    try {
      const response = await api.get(`/api/events/photographer/${user.id}/subscriptions`);
      if (response.data.success) {
        // Filtrar apenas inscrições pendentes (garantia dupla)
        const pendingOnly = (response.data.data || []).filter(
          (event: SubscribedEvent) => event.subscriptionStatus === 'Pending'
        );
        setSubscribedEvents(pendingOnly);
      }
    } catch (error) {
      console.error('Erro ao buscar inscrições:', error);
    }
  };

  const fetchPhotographers = async () => {
    try {
      const response = await api.get('/api/auth/photographers');
      if (response.data.success) {
        setPhotographers(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar fotógrafos:', error);
    }
  };

  const handlePhotographerChange = (photographerId: string) => {
    setSelectedPhotographer(photographerId);
    fetchEvents(isAdmin, photographerId);
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      Draft: { label: 'Rascunho', className: 'bg-gray-100 text-gray-800' },
      PendingApproval: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-800' },
      Approved: { label: 'Aprovado', className: 'bg-green-100 text-green-800' },
      Rejected: { label: 'Rejeitado', className: 'bg-red-100 text-red-800' },
      Closed: { label: 'Encerrado', className: 'bg-gray-100 text-gray-800' },
      Pending: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-800' }, // Para inscrições
    };

    const statusInfo = statusMap[status as keyof typeof statusMap] || statusMap.Draft;
    return <Badge className={statusInfo.className}>{statusInfo.label}</Badge>;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return '-';
      }
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Erro ao formatar data:', dateString, error);
      return '-';
    }
  };

  const formatDateOnly = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return '-';
      }
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Erro ao formatar data:', dateString, error);
      return '-';
    }
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.location?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || event.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Preparar dados para agrupamento
  const ownerEvents = filteredEvents.filter(e => e.isOwner === true);
  const subscribedEventsList = filteredEvents.filter(e => e.isSubscribed === true && e.isOwner !== true);

  // Configurar grupos (apenas se houver eventos para cada grupo)
  const tableGroups: DataTableGroup<EventWithRelationship>[] = [];
  
  if (ownerEvents.length > 0) {
    tableGroups.push({
      key: 'owner',
      label: 'Proprietário',
      items: ownerEvents,
      defaultExpanded: true,
    });
  }
  
  if (subscribedEventsList.length > 0) {
    tableGroups.push({
      key: 'subscribed',
      label: 'Inscrição',
      items: subscribedEventsList,
      defaultExpanded: true,
    });
  }

  // Configurar colunas
  const columns: DataTableColumn<EventWithRelationship>[] = [
    {
      key: 'event',
      label: 'Evento',
      render: (item) => (
        <div className="flex items-center gap-3">
          {item.coverimage ? (
            <img src={item.coverimage} alt="" className="w-10 h-10 rounded object-cover" />
          ) : (
            <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-slate-400" />
            </div>
          )}
          <div>
            <div className="font-medium">{item.title}</div>
            <div className="text-xs text-muted-foreground">{item.location}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'date',
      label: 'Data',
      render: (item) => formatDateOnly(item.date),
    },
    {
      key: 'status',
      label: 'Status',
      render: (item) => getStatusBadge(item.status || 'Draft'),
    },
    {
      key: 'relationship',
      label: 'Relacionamento',
      render: (item) => {
        if (item.isOwner) {
          return <Badge variant="default" className="text-xs bg-primary text-primary-foreground">Proprietário</Badge>;
        }
        if (item.isSubscribed) {
          return <Badge variant="secondary" className="text-xs bg-secondary text-secondary-foreground">Inscrito</Badge>;
        }
        return <span className="text-xs text-muted-foreground">-</span>;
      },
    },
  ];

  // Ações contextuais por linha (apenas ícones)
  const rowActions = (event: EventWithRelationship) => (
    <div className="flex justify-end gap-1">
      {isAdmin && event.status === 'PendingApproval' && (
        <>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
            onClick={() => handleApproveEvent(event.id)}
            title="Aprovar"
          >
            <UserCheck className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={() => handleRejectEvent(event.id)}
            title="Rejeitar"
          >
            <X className="h-4 w-4" />
          </Button>
        </>
      )}
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
        onClick={() => setLocation(`/event/${event.id}`)}
        title="Ver Detalhes"
      >
        <Eye className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
        onClick={() => setLocation(`/events/${event.id}/edit`)}
        title="Editar"
      >
        <Edit className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
        onClick={() => setLocation(`/uploader/dashboard?eventId=${event.id}`)}
        title="Upload Fotos"
      >
        <Upload className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
        onClick={() => handleDeleteEvent(event.id)}
        title="Excluir"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );

  // Área de filtros
  const filterArea = (
    <div className="flex flex-col md:flex-row gap-4">
      <div className="flex-1">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Buscar eventos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>
      <div className="flex gap-2">
        {isAdmin && (
          <Select value={selectedPhotographer} onValueChange={handlePhotographerChange}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Selecionar fotógrafo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os fotógrafos</SelectItem>
              {photographers.map((photographer) => (
                <SelectItem key={photographer.id} value={photographer.id}>
                  {photographer.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-input bg-background rounded-md text-sm"
        >
          <option value="all">Todos os status</option>
          <option value="Draft">Rascunho</option>
          <option value="PendingApproval">Pendente</option>
          <option value="Approved">Aprovado</option>
          <option value="Rejected">Rejeitado</option>
          <option value="Closed">Encerrado</option>
        </select>
      </div>
    </div>
  );

  // Área de ações
  const actionsArea = (
    <>
      <Button variant="outline" onClick={() => fetchEvents(isAdmin, selectedPhotographer)}>
        <Filter className="h-4 w-4 mr-2" />
        Atualizar
      </Button>
    </>
  );

  const tableConfig: DataTableConfig = {
    showFilters: true,
    showCheckboxes: false,
    showGrouping: true,
    showFooter: true,
    compact: true,
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (!confirm('Tem certeza que deseja excluir este evento?')) return;
    try {
      const response = await api.delete(`/api/events/${eventId}`);
      if (response.data.success) {
        toast({ title: 'Evento excluído', description: 'O evento foi excluído com sucesso.' });
        fetchEvents(isAdmin, selectedPhotographer);
      }
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível excluir o evento.', variant: 'destructive' });
    }
  };

  const handleApproveEvent = async (eventId: string) => {
    try {
      const response = await api.post(`/api/events/${eventId}/approve`);
      if (response.data.success) {
        toast({ title: 'Evento aprovado', description: 'O evento foi aprovado.' });
        fetchEvents(isAdmin, selectedPhotographer);
      }
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível aprovar o evento.', variant: 'destructive' });
    }
  };

  const handleRejectEvent = async (eventId: string) => {
    if (!confirm('Tem certeza que deseja rejeitar este evento?')) return;
    try {
      const response = await api.post(`/api/events/${eventId}/reject`);
      if (response.data.success) {
        toast({ title: 'Evento rejeitado', description: 'O evento foi rejeitado.' });
        fetchEvents(isAdmin, selectedPhotographer);
      }
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível rejeitar o evento.', variant: 'destructive' });
    }
  };

  const handleCancelSubscription = async (eventId: string) => {
    if (!confirm('Tem certeza que deseja cancelar sua inscrição neste evento?')) return;
    try {
      const response = await api.delete(`/api/events/${eventId}/subscribe`);
      if (response.data.success) {
        toast({ title: 'Inscrição cancelada', description: 'Sua inscrição foi cancelada.' });
        fetchSubscribedEvents();
      }
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível cancelar a inscrição.', variant: 'destructive' });
    }
  };

  const handleApprovePhotographer = async (eventId: string, photographerId: string) => {
    try {
      const response = await api.patch(`/api/events/${eventId}/photographers/${photographerId}`, { status: 'Approved' });
      if (response.data.success) {
        toast({ title: 'Fotógrafo aprovado', description: 'A inscrição foi aprovada com sucesso.' });
        fetchPendingSubscriptions();
        fetchEvents(isAdmin, selectedPhotographer);
      }
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível aprovar a inscrição.', variant: 'destructive' });
    }
  };

  const handleRejectPhotographer = async (eventId: string, photographerId: string) => {
    if (!confirm('Tem certeza que deseja rejeitar esta inscrição?')) return;
    try {
      const response = await api.patch(`/api/events/${eventId}/photographers/${photographerId}`, { status: 'Rejected' });
      if (response.data.success) {
        toast({ title: 'Inscrição rejeitada', description: 'A inscrição foi rejeitada.' });
        fetchPendingSubscriptions();
      }
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível rejeitar a inscrição.', variant: 'destructive' });
    }
  };

  if (isLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando eventos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Gestão de Eventos</h1>
            <p className="text-muted-foreground">Gerencie seus eventos e inscrições</p>
          </div>
          <Button
            onClick={() => setLocation('/events/create')}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Criar Evento
          </Button>
        </div>

        {/* Seção de Inscrições Pendentes (Apenas Admin) */}
        {isAdmin && pendingSubscriptions.length > 0 && (
          <Card className="mb-8 border-blue-200 bg-blue-50/50">
            <CardHeader>
              <CardTitle className="flex items-center text-blue-800">
                <UserCheck className="w-5 h-5 mr-2" />
                Inscrições Pendentes de Aprovação ({pendingSubscriptions.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Evento</TableHead>
                    <TableHead>Fotógrafo</TableHead>
                    <TableHead>Data do Evento</TableHead>
                    <TableHead>Data da Inscrição</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingSubscriptions.map((subscription) => (
                    <TableRow key={subscription.id}>
                      <TableCell className="font-medium">
                        {subscription.event ? (
                          <div className="flex items-center gap-3">
                            {subscription.event.coverimage ? (
                              <img src={subscription.event.coverimage} alt="" className="w-10 h-10 rounded object-cover" />
                            ) : (
                              <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center">
                                <Calendar className="w-5 h-5 text-slate-400" />
                              </div>
                            )}
                            <div>
                              <div className="font-medium">{subscription.event.title}</div>
                              <div className="text-xs text-muted-foreground">{subscription.event.location || '-'}</div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Evento não encontrado</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{subscription.photographer.name}</div>
                          <div className="text-xs text-muted-foreground">{subscription.photographer.email}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {subscription.event ? formatDate(subscription.event.date) : '-'}
                      </TableCell>
                      <TableCell>{formatDate(subscription.createdAt)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-green-600 border-green-200 hover:bg-green-50" 
                            onClick={() => handleApprovePhotographer(subscription.eventId, subscription.photographerId)}
                          >
                            Aprovar
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-red-600 border-red-200 hover:bg-red-50" 
                            onClick={() => handleRejectPhotographer(subscription.eventId, subscription.photographerId)}
                          >
                            Rejeitar
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Seção de Inscrições Pendentes (Apenas Fotógrafos não-admin) */}
        {(() => {
          const pendingSubscriptions = subscribedEvents.filter(e => e.subscriptionStatus === 'Pending');
          return !isAdmin && pendingSubscriptions.length > 0 && (
            <Card className="mb-8 border-yellow-200 bg-yellow-50/50">
              <CardHeader>
                <CardTitle className="flex items-center text-yellow-800">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  Minhas Inscrições Pendentes ({pendingSubscriptions.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Evento</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Status da Inscrição</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingSubscriptions.map((event) => (
                    <TableRow key={event.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          {event.coverimage ? (
                            <img src={event.coverimage} alt="" className="w-10 h-10 rounded object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center">
                              <Calendar className="w-5 h-5 text-slate-400" />
                            </div>
                          )}
                          <div>
                            <div className="font-medium">{event.title || '-'}</div>
                            <div className="text-xs text-muted-foreground">{event.location || '-'}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(event.date)}</TableCell>
                      <TableCell>{getStatusBadge(event.subscriptionStatus)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => setLocation(`/event/${event.id}`)}
                            title="Visualizar Detalhes"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => setLocation(`/uploader/dashboard?eventId=${event.id}`)}
                            title="Adicionar Fotos"
                          >
                            <Upload className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleCancelSubscription(event.id)}
                            title="Cancelar Inscrição"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          );
        })()}

        {/* Lista de Eventos usando DataTable */}
        <DataTable
          title={isAdmin && selectedPhotographer === 'all' 
            ? 'Todos os Eventos' 
            : 'Meus Eventos'}
          description={!isAdmin ? 'Eventos que você criou e eventos onde você está inscrito e aprovado' : undefined}
          data={filteredEvents}
          columns={columns}
          groups={tableGroups}
          filterArea={filterArea}
          actionsArea={actionsArea}
          rowActions={rowActions}
          config={tableConfig}
          emptyMessage="Nenhum evento encontrado"
          getItemId={(item) => item.id}
        />
      </div>
    </div>
  );
}
